import type { FC } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import EulaAgreement from 'components/EulaAgreement/EulaAgreement';
import { MainHeader } from 'components/Headers/MainHeader';
import { MainHeaderWithTabs } from 'components/Headers/MainHeaderWithTabs';
import { useAuth } from 'contexts/AuthContext';
import { OnboardingTourProvider } from 'contexts/OnboardingTourContext';
import { TicketsProvider } from 'contexts/TicketsContext';
import { UserProfileProvider } from 'contexts/UserProfileContext';
import { RoutePathsE } from 'declarations/enums/routes';
import LoginPage from 'pages/LoginPage/LoginPage';
import ProfilePage from 'pages/ProfilePage/ProfilePage';
import ReleasesPage from 'pages/ReleasesPage/ReleasesPage';
import TicketDetailViewPage from 'pages/TicketsPage/components/TicketDetailViewPage/TicketDetailViewPage';
import TicketsPageLayout from 'pages/TicketsPage/components/TicketsPageLayout/TicketsPageLayout';
import TicketsPage from 'pages/TicketsPage/TicketsPage';

import PublicRoutes from './components/PublicRoutes';
import RequireAuth from './components/RequireAuth';

const AppRoutes: FC = () => {
  const { isUserLoggedIn } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path={RoutePathsE.LOGIN_PAGE}
        element={
          <PublicRoutes>
            <LoginPage />
          </PublicRoutes>
        }
      />

      {/* Private routes */}
      <Route
        element={
          <RequireAuth>
            <UserProfileProvider>
              <TicketsProvider>
                <OnboardingTourProvider>
                  <MainHeaderWithTabs />
                  <EulaAgreement />
                  <Outlet />
                </OnboardingTourProvider>
              </TicketsProvider>
            </UserProfileProvider>
          </RequireAuth>
        }
      >
        <Route path={RoutePathsE.TICKETS_PAGE} element={<TicketsPageLayout />}>
          <Route index element={<TicketsPage />} />
          <Route path="ticket/:ticketId" element={<TicketDetailViewPage />} />
        </Route>
      </Route>
      <Route
        path={RoutePathsE.PROFILE_PAGE}
        element={
          <RequireAuth>
            <UserProfileProvider>
              <MainHeader />
              <ProfilePage />
            </UserProfileProvider>
          </RequireAuth>
        }
      />
      <Route
        path={RoutePathsE.RELEASES_PAGE}
        element={
          <RequireAuth>
            <UserProfileProvider>
              <MainHeader />
              <ReleasesPage />
            </UserProfileProvider>
          </RequireAuth>
        }
      />

      {/* Redirect if user tries to visit not defined route */}
      <Route
        path="*"
        element={
          <Navigate
            replace
            to={isUserLoggedIn ? RoutePathsE.TICKETS_PAGE : RoutePathsE.LOGIN_PAGE}
          />
        }
      />
    </Routes>
  );
};

export default AppRoutes;
